import React, { useState, useEffect } from 'react';
import { ArrowLeft, ExternalLink, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { steelCementRatesService } from '../../services/api/steelCementRatesService';
import { resolveDocUrl } from './Steelcementratesform';

interface SteelCementRate {
  id: number;
  year: number;
  month: number;
  document_title: string;
  document_url: string;
  description?: string;
  is_active: boolean;
  display_order: number;
}

interface GroupedRates {
  [year: number]: SteelCementRate[];
}

interface RateStatistics {
  totalDocuments: number;
  totalYears: number;
  yearRange: {
    start: number;
    end: number;
  } | null;
}

const SteelCementRatesView: React.FC = () => {
  const navigate = useNavigate();
  const [rates, setRates] = useState<SteelCementRate[]>([]);
  const [groupedRates, setGroupedRates] = useState<GroupedRates>({});
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<RateStatistics>({
    totalDocuments: 0,
    totalYears: 0,
    yearRange: null,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [ratesResponse, statsResponse] = await Promise.all([
        steelCementRatesService.getAll(),
        steelCementRatesService.getStatistics(),
      ]);

      const data = ratesResponse.data || [];
      setRates(data);
      setStats(statsResponse.data);

      // Group by year
      const grouped = data.reduce((acc: GroupedRates, rate: SteelCementRate) => {
        if (!acc[rate.year]) {
          acc[rate.year] = [];
        }
        acc[rate.year].push(rate);
        return acc;
      }, {});

      // Sort months within each year
      Object.keys(grouped).forEach((year) => {
        grouped[parseInt(year)].sort((a, b) => a.month - b.month);
      });

      setGroupedRates(grouped);
    } catch (error) {
      toast.error('Failed to fetch steel & cement rates');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="inline-block w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-gray-600">Loading steel & cement rates...</p>
        </div>
      </div>
    );
  }

  const years = Object.keys(groupedRates).map(Number).sort((a, b) => a - b);

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Steel & Cement Rates</h1>
          <p className="mt-1 text-sm text-gray-600">
            Historical steel and cement rate documents from {stats.yearRange?.start} to{' '}
            {stats.yearRange?.end}
          </p>
        </div>
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
        >
          <ArrowLeft size={18} className="mr-2" />
          Back
        </button>
      </div>

      {/* Main Content */}
      <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                  S.No.
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                  Year
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Documents
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {years.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-8 text-center text-gray-500">
                    No steel & cement rate documents available
                  </td>
                </tr>
              ) : (
                years.map((year, yearIndex) => {
                  const yearRates = groupedRates[year];
                  return (
                    <React.Fragment key={year}>
                      {yearRates.map((rate, rateIndex) => (
                        <tr key={rate.id} className="hover:bg-gray-50 transition-colors">
                          {rateIndex === 0 && (
                            <>
                              <td
                                rowSpan={yearRates.length}
                                className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 border-r border-gray-200"
                              >
                                {yearIndex + 1}
                              </td>
                              <td
                                rowSpan={yearRates.length}
                                className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 border-r border-gray-200"
                              >
                                {year}
                              </td>
                            </>
                          )}
                          <td className="px-6 py-4 text-sm text-gray-900">
                            <div className="flex items-center">
                              <FileText size={16} className="mr-2 text-gray-400 flex-shrink-0" />
                              {rate.document_title}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <a
                              href={resolveDocUrl(rate.document_url)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium"
                            >
                              View
                              <ExternalLink size={14} className="ml-1" />
                            </a>
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Years</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{stats.totalYears}</p>
            </div>
            <div className="p-3 rounded-lg bg-blue-100">
              <FileText size={24} className="text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Documents</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{stats.totalDocuments}</p>
            </div>
            <div className="p-3 rounded-lg bg-green-100">
              <FileText size={24} className="text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Year Range</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">
                {stats.yearRange
                  ? `${stats.yearRange.start}-${stats.yearRange.end}`
                  : '-'}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-purple-100">
              <FileText size={24} className="text-purple-600" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SteelCementRatesView;